import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Mail, Calendar, LogOut, Camera, Loader2, Music, Headphones, Pencil, Check, X, Play, Eye, Trash2, Settings, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { FirstBeatBadge, FirstBeatBadgeMini } from '../components/FirstBeatBadge';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

const BANNER_IMAGES = [
  "https://images.unsplash.com/photo-1762267684555-8250b6f7df2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbmVvbiUyMGFic3RyYWN0JTIwY29uY2VydCUyMHN0YWdlJTIwYmFubmVyJTIwd2lkZXxlbnwwfHx8fDE3NzUxODYxODR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1703795799612-27dbd1a9e17b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMGFic3RyYWN0JTIwY29uY2VydCUyMHN0YWdlJTIwYmFubmVyJTIwd2lkZXxlbnwwfHx8fDE3NzUxODYxODR8MA&ixlib=rb-4.1.0&q=85",
];

const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg",
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const formatNum = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

export const ProfilePage = ({ embedded = false }) => {
  const { user, logout, token, axiosInstance, refreshUser } = useAuth();
  const { playVideo } = usePlayer();
  const navigate = useNavigate();
  
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [newBio, setNewBio] = useState('');
  const [savingBio, setSavingBio] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchMyVideos = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/creator/my-videos');
      setVideos(res.data.filter(v => !v.is_deleted));
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoadingVideos(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (user) {
      fetchMyVideos();
    }
  }, [user, fetchMyVideos]);

  if (!user) return null;

  const isArtist = user.role === 'artist';
  const displayName = user.display_name || user.username;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAvatarUrl = () => {
    if (user.avatar) return `${API}/api/files/${user.avatar}`;
    return null;
  };

  const getBannerUrl = () => {
    if (user.banner) return `${API}/api/files/${user.banner}`;
    return BANNER_IMAGES[0];
  };

  const getThumbnail = (video) => {
    // Single source of truth: custom > Bunny > legacy → AUDIVUE branded placeholder.
    return resolveThumbnailUrl(video);
  };

  // Avatar upload
  const handleAvatarClick = () => avatarInputRef.current?.click();
  
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await axiosInstance.post('/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Avatar updated!');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  // Banner upload
  const handleBannerClick = () => bannerInputRef.current?.click();
  
  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Banner must be under 10MB'); return; }

    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('banner', file);
      await axiosInstance.post('/profile/banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Banner updated!');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  // Name editing
  const startEditingName = () => {
    setNewDisplayName(displayName);
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNewDisplayName('');
  };

  const saveDisplayName = async () => {
    if (!newDisplayName.trim()) { toast.error('Name cannot be empty'); return; }
    setSavingName(true);
    try {
      await axiosInstance.put('/profile/display-name', { display_name: newDisplayName.trim() });
      toast.success('Name updated!');
      setEditingName(false);
      if (refreshUser) await refreshUser();
      fetchMyVideos();
    } catch (err) {
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const startEditingBio = () => {
    setNewBio(user.bio || '');
    setEditingBio(true);
  };

  const saveBio = async () => {
    setSavingBio(true);
    try {
      await axiosInstance.put('/profile/update', null, { params: { bio: newBio.trim() } });
      toast.success('Bio updated!');
      setEditingBio(false);
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error('Failed to update bio');
    } finally {
      setSavingBio(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill in both fields');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axiosInstance.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Video actions
  const handleVideoClick = (video) => {
    playVideo(video, videos, videos.indexOf(video));
  };

  const handleDelete = (video) => setDeleteConfirm(video);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axiosInstance.delete(`/videos/${deleteConfirm.id}`);
      toast.success('Video deleted');
      setDeleteConfirm(null);
      fetchMyVideos();
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const totalViews = videos.reduce((sum, v) => sum + (v.views || 0), 0);
  const totalLikes = videos.reduce((sum, v) => sum + (v.likes || 0), 0);

  return (
    <div className="min-h-screen bg-black">
      {/* Hidden file inputs */}
      <input type="file" ref={avatarInputRef} onChange={handleAvatarChange} accept="image/*" className="hidden" />
      <input type="file" ref={bannerInputRef} onChange={handleBannerChange} accept="image/*" className="hidden" />

      {/* Banner */}
      <div 
        className="relative h-48 md:h-64 bg-gradient-to-br from-zinc-900 to-black cursor-pointer group"
        onClick={handleBannerClick}
      >
        <img src={getBannerUrl()} alt="Banner" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {uploadingBanner ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <div className="bg-black/60 px-4 py-2 rounded-lg flex items-center gap-2 text-white text-sm">
              <Camera className="w-4 h-4" /> Change Banner
            </div>
          )}
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative px-6 pb-6 -mt-20">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          {/* Avatar */}
          <div 
            className="relative cursor-pointer group"
            onClick={handleAvatarClick}
          >
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-black overflow-hidden bg-zinc-800">
              {getAvatarUrl() ? (
                <img src={getAvatarUrl()} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-700 to-zinc-900">
                  <User className="w-16 h-16 text-zinc-500" />
                </div>
              )}
            </div>
            <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
              {uploading ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : (
                <Camera className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            {/* Name */}
            <div className="flex items-center gap-3 mb-2">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white text-2xl font-bold w-64 h-10"
                    placeholder="Your name"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveDisplayName();
                      if (e.key === 'Escape') cancelEditingName();
                    }}
                  />
                  <button onClick={saveDisplayName} disabled={savingName}
                    className="p-2 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30">
                    {savingName ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  </button>
                  <button onClick={cancelEditingName}
                    className="p-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">{displayName}</h1>
                  <button onClick={startEditingName}
                    className="p-2 rounded-full hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
                    title="Edit name">
                    <Pencil className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Role & username */}
            <div className="flex items-center gap-3 mb-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                isArtist ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
              }`}>
                {isArtist ? <Music className="w-4 h-4" /> : <Headphones className="w-4 h-4" />}
                {isArtist ? 'Artist' : 'Listener'}
              </span>
              {user.has_first_beat && <FirstBeatBadgeMini />}
              <span className="text-zinc-500">@{user.username}</span>
            </div>

            {/* Stats (for artists) */}
            {isArtist && (
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-white font-bold">{videos.length}</span>
                  <span className="text-zinc-500 ml-1">Videos</span>
                </div>
                <div>
                  <span className="text-white font-bold">{formatNum(totalViews)}</span>
                  <span className="text-zinc-500 ml-1">Views</span>
                </div>
                <div>
                  <span className="text-white font-bold">{formatNum(totalLikes)}</span>
                  <span className="text-zinc-500 ml-1">Likes</span>
                </div>
              </div>
            )}

            {/* Bio / About */}
            <div className="mt-4" data-testid="bio-section">
              {editingBio ? (
                <div className="space-y-2">
                  <textarea
                    value={newBio}
                    onChange={(e) => setNewBio(e.target.value)}
                    placeholder="Tell your audience about yourself..."
                    maxLength={1000}
                    rows={5}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none"
                    autoFocus
                    data-testid="bio-input"
                  />
                  <div className="flex items-center gap-2">
                    <button onClick={saveBio} disabled={savingBio}
                      className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium flex items-center gap-1"
                      data-testid="bio-save-btn">
                      {savingBio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Save
                    </button>
                    <button onClick={() => setEditingBio(false)}
                      className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-sm">
                      Cancel
                    </button>
                    <span className="text-xs text-zinc-600 ml-auto">{newBio.length}/1000</span>
                  </div>
                </div>
              ) : (
                <div className="group flex items-start gap-2">
                  {user.bio ? (
                    <p className="text-sm text-zinc-400 leading-relaxed">{user.bio}</p>
                  ) : (
                    <p className="text-sm text-zinc-600 italic">Add a bio to tell your audience about yourself</p>
                  )}
                  <button onClick={startEditingBio}
                    className="p-1.5 rounded-full hover:bg-zinc-800 text-zinc-600 hover:text-white transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                    title="Edit bio" data-testid="bio-edit-btn">
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setShowSettings(!showSettings)}
              className="border-zinc-700 text-white hover:bg-zinc-800">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button variant="outline" onClick={() => { logout(); navigate('/login'); }}
              className="border-zinc-700 text-red-400 hover:bg-red-500/10 hover:text-red-300">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      {/* Settings Panel (collapsible) */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Account Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-zinc-500" />
                      <span className="text-zinc-400">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-zinc-500" />
                      <span className="text-zinc-400">Joined {formatDate(user.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Change Password */}
                <div className="border-t border-zinc-800 pt-5">
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-zinc-500" /> Change Password
                  </h4>
                  <div className="space-y-3 max-w-sm">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current password"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      data-testid="current-password-input"
                    />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password (min 6 chars)"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      data-testid="new-password-input"
                    />
                    <Button
                      onClick={handleChangePassword}
                      disabled={changingPassword || !currentPassword || !newPassword}
                      className="bg-orange-500 hover:bg-orange-600 text-white text-sm py-2 px-4 rounded-lg"
                      data-testid="change-password-btn"
                    >
                      {changingPassword ? 'Changing...' : 'Update Password'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Videos Section (for artists) */}
      {isArtist && (
        <div className="px-6 pb-24">
          <h2 className="text-xl font-bold text-white mb-4">My Videos</h2>
          
          {loadingVideos ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/30 rounded-xl border border-zinc-800">
              <Music className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 mb-4">No videos yet</p>
              <Button onClick={() => navigate('/studio')} className="bg-white text-black hover:bg-zinc-200">
                Upload Your First Video
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {videos.map((video, i) => (
                <div key={video.id} className="group relative">
                  <div className="cursor-pointer" onClick={() => handleVideoClick(video)}>
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 mb-2">
                      <img src={getThumbnail(video, i)} alt={video.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                          <Play className="w-6 h-6 text-black fill-black ml-1" />
                        </div>
                      </div>
                      {/* Views badge */}
                      <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-0.5 rounded text-xs text-white flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {formatNum(video.views || 0)}
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-white truncate">{video.title}</p>
                    <p className="text-xs text-zinc-500">{video.genre}</p>
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(video); }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 rounded-xl p-6 max-w-sm w-full border border-zinc-800"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-2">Delete Video?</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Are you sure you want to delete "{deleteConfirm.title}"? This cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}
                  className="flex-1 border-zinc-700 text-white">
                  Cancel
                </Button>
                <Button onClick={confirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Iter 123: "Become a Creator" CTA replaced with a friendlier
          "Start uploading" nudge — the approval flow is gone. Any user
          who taps this hits /studio directly; the backend auto-promotes
          their role to `artist` on first upload. */}
      {!isArtist && (
        <div className="px-6 pb-10">
          <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-orange-500/15 via-pink-500/10 to-fuchsia-500/15 p-6 md:p-7">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-orange-500/20 blur-3xl pointer-events-none" />
            <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.25em] text-orange-300 mb-2">Share your music</p>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Start uploading</h2>
              <p className="text-sm text-zinc-300 mb-5 max-w-md">
                Drop your first AI-generated music video and get discovered by the AUDIVUE community. No approval needed.
              </p>
              <Button
                onClick={() => navigate('/studio')}
                className="bg-white text-black hover:bg-zinc-200 font-semibold rounded-full"
                data-testid="profile-start-uploading-cta"
              >
                Go to Studio
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* First Beat Badge (listeners) */}
      {!isArtist && user.has_first_beat && (
        <div className="px-6 pb-24">
          <h2 className="text-xl font-bold text-white mb-4">Your Badges</h2>
          <FirstBeatBadge />
        </div>
      )}
    </div>
  );
};
